// oklch-fallback.ts — manual sRGB → OKLCH → clamp-lightness → sRGB.
//
// Layer 3 graceful degradation for the OKLCH relative-color CSS line in
// `index.css`:
//
//   :root  { --color-primary-display: var(--color-primary); }
//   .dark  { --color-primary-display: oklch(from var(--color-primary) max(l, 0.65) c h); }
//
// Native CSS handles Layer 2 (@supports) on modern browsers; this module is the
// fallback for locked-enterprise Chrome 100-118 + Firefox < 128 + Safari < 18.
// Called at theme-apply time (once per project-load) — not a hot path.
//
// Math reference: Björn Ottosson's OKLab construction
// (https://bottosson.github.io/posts/oklab/) + CSS Color Module 4 specs. The
// matrices below are the published constants — do not derive on the fly.
//
// Tripwire: if a second runtime color operation lands in this codebase
// (contrast auto-pick, gamut mapping, palette interpolation), re-evaluate vs
// `@texel/color` (~3.5 KB) at that point. Single operation + air-gap (ADR-013)
// + narrow audience tips manual today.

const HEX_RE = /^#([0-9a-fA-F]{6})$/;

interface Lab { L: number; a: number; b: number }
interface Lch { L: number; C: number; H: number }
interface Rgb { r: number; g: number; b: number }

// sRGB gamma decode (0..1 nonlinear → 0..1 linear).
function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

// sRGB gamma encode (0..1 linear → 0..1 nonlinear).
function linearToSrgb(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}

function hexToRgb(hex: string): Rgb {
  const m = HEX_RE.exec(hex);
  if (!m) throw new Error(`oklch-fallback: invalid hex "${hex}" (expected #rrggbb)`);
  const h = m[1]!;
  return {
    r: parseInt(h.slice(0, 2), 16) / 255,
    g: parseInt(h.slice(2, 4), 16) / 255,
    b: parseInt(h.slice(4, 6), 16) / 255,
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const ch = (v: number) => {
    const n = Math.max(0, Math.min(255, Math.round(v * 255)));
    return n.toString(16).padStart(2, '0');
  };
  return `#${ch(r)}${ch(g)}${ch(b)}`;
}

// Linear sRGB → OKLab (Ottosson constants).
function linearRgbToOklab(r: number, g: number, b: number): Lab {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);
  return {
    L: 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_,
  };
}

// OKLab → linear sRGB.
function oklabToLinearRgb(L: number, a: number, b: number): Rgb {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.2914855480 * b;
  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;
  return {
    r:  4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
  };
}

function labToLch({ L, a, b }: Lab): Lch {
  return { L, C: Math.sqrt(a * a + b * b), H: Math.atan2(b, a) };
}

function lchToLab({ L, C, H }: Lch): Lab {
  return { L, a: C * Math.cos(H), b: C * Math.sin(H) };
}

function hexToOklch(hex: string): Lch {
  const { r, g, b } = hexToRgb(hex);
  const lin = { r: srgbToLinear(r), g: srgbToLinear(g), b: srgbToLinear(b) };
  return labToLch(linearRgbToOklab(lin.r, lin.g, lin.b));
}

function oklchToHex(lch: Lch): string {
  const { L, a, b } = lchToLab(lch);
  const lin = oklabToLinearRgb(L, a, b);
  return rgbToHex({
    r: linearToSrgb(lin.r),
    g: linearToSrgb(lin.g),
    b: linearToSrgb(lin.b),
  });
}

/**
 * Clamps a hex color's OKLCH lightness to at least `floor`, preserving chroma
 * and hue. Returns the new hex. Matches the native CSS one-liner
 * `oklch(from var(--color-primary) max(l, floor) c h)` for browsers without
 * relative-color syntax support.
 *
 * Default floor `0.65` per DESIGN-TOKENS §Dark mode.
 */
export function clampLightness(hex: string, floor = 0.65): string {
  const lch = hexToOklch(hex);
  if (lch.L >= floor) return hex.toLowerCase();
  return oklchToHex({ L: floor, C: lch.C, H: lch.H });
}

// Exposed for tests + future contrast helpers.
export const _internal = { hexToOklch, oklchToHex, hexToRgb, rgbToHex };

/**
 * A hex's OKLCH hue (degrees, 0–360) + chroma — for hue-proximity advisories
 * (warn when a project brand sits on top of a Layer-3 semantic hue, so a
 * red-brand's primary button doesn't read as a danger button). Chroma lets the
 * check ignore near-greys (no meaningful hue to clash).
 */
export function oklchHC(hex: string): { h: number; c: number } {
  const { C, H } = hexToOklch(hex);
  return { h: ((H * 180) / Math.PI + 360) % 360, c: C };
}

/**
 * WCAG 2.x relative luminance + contrast ratio (sRGB), for the JS-derived
 * brand tokens that CSS cannot compute (no contrast-color() yet).
 */
export function contrastRatio(hexA: string, hexB: string): number {
  const lum = (hex: string): number => {
    const { r, g, b } = hexToRgb(hex);
    return 0.2126 * srgbToLinear(r) + 0.7152 * srgbToLinear(g) + 0.0722 * srgbToLinear(b);
  };
  const la = lum(hexA);
  const lb = lum(hexB);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

// The two ink candidates for any brand fill — near-black + white. Shared by the
// author-time derive (deriveOnPrimary) and the display-time derive
// (deriveOnDisplay). This file IS the colour-derivation engine, so these raw hex
// candidates are the derivation's SOURCE values, not styling call sites — the
// same exemption class as the token definitions in index.css / branding.ts.
/* eslint-disable local/no-raw-color -- ink candidates: source values of the derivation engine */
const INK_DARK = '#0f172a';
const INK_BLACK = '#000000';
const INK_LIGHT = '#ffffff';
/* eslint-enable local/no-raw-color */

/** WCAG 2.x AA contrast floor for normal text — button labels included. */
export const AA_CONTRAST = 4.5;

/**
 * The legible ink for a brand fill, chosen at AUTHOR time (DESIGN-TOKENS §Color): whichever of
 * near-black / white contrasts better ON the fill. This is what the project
 * create/edit form stores as `color_on_primary` — the model is ONE brand picker +
 * a derived, always-max-contrast ink, not a second colour the admin can get wrong
 * (many telecom brands' marketing ink — white on a mid-tone red — is sub-AA).
 * `deriveOnDisplay` then honours this stored ink at render, flipping only when the
 * dark-mode lightness clamp lifts the fill out of its reach.
 *
 * Uses PURE black (not the softer near-black of the display flip) as the dark
 * candidate: the stored value must MAXIMISE contrast, and on a saturated mid-tone
 * (e.g. #ed1c24) near-black falls ~0.4 short of AA while pure black clears it.
 */
export function deriveOnPrimary(fillHex: string): string {
  return contrastRatio(fillHex, INK_BLACK) >= contrastRatio(fillHex, INK_LIGHT) ? INK_BLACK : INK_LIGHT;
}

/**
 * The ink rendered ON a brand fill as actually displayed. The admin's stored
 * ink is honored while it clears WCAG AA (4.5:1) on the given fill; when the
 * dark-mode lightness clamp lifts the fill out of the stored ink's reach
 * (white-on-lifted-fill never reaches AA), the ink flips to whichever of
 * near-black / white contrasts better — the same per-fill-ink pattern the
 * Layer-3 on-<semantic> tokens use (DESIGN-TOKENS §Color).
 */
export function deriveOnDisplay(displayHex: string, storedInk: string): string {
  if (contrastRatio(displayHex, storedInk) >= AA_CONTRAST) return storedInk;
  return contrastRatio(displayHex, INK_DARK) >= contrastRatio(displayHex, INK_LIGHT)
    ? INK_DARK
    : INK_LIGHT;
}
